"use client";

/**
 * use-avatar-reactivity — shared tilt + audio signal source for the
 * GenerativeArtistAvatar's musical patterns.
 *
 * Design rules:
 *
 *   • ONE RAF loop, ever. Module-level state is updated 30 times a second
 *     and pushed into subscribed SVG elements via CSS custom properties.
 *     If 100 avatars are on screen we still only run one RAF.
 *
 *   • Subscribers receive a ref-driven mutation, NOT a React state update.
 *     That means no re-renders per frame — performance scales with on-
 *     screen avatars regardless of complexity.
 *
 *   • RAF auto-stops when the subscriber count drops to zero OR the tab
 *     is hidden (visibilitychange).
 *
 *   • Sound only (Gordon 2026-11-04: "stop all effects when music is not
 *     playing … all effects should react with music … even the coπ mic
 *     signal in should affect the artwork. All sound"): the player's
 *     analyser while something plays, else the level BB publishes from a
 *     microphone or the Academy synth (window.__bbMicDb / __bbMicOn — coπ
 *     listening, the tuner…), else silence — everything settles and stands
 *     still. `bb-sound` (window event, detail: boolean) says when sound starts
 *     or stops, so SVG drift animations can pause with it.
 *
 * Exposed CSS vars on each subscribed <svg>:
 *
 *   --bb-energy   smoothed RMS amplitude, 0..1
 *   --bb-tilt-x   -1..1 (device γ on mobile, mouse-X on desktop)
 *   --bb-tilt-y   -1..1 (device β on mobile, mouse-Y on desktop)
 *   --bb-band-0 … --bb-band-7   8 frequency-bucket levels, 0..1
 *
 * Patterns consume these in `transform`, `scale`, `opacity`, `stroke-width`
 * via inline style — SVG attribute values can't read CSS vars directly, so
 * we always wrap in `calc()` inside a `style` prop.
 */

import { useEffect } from "react";
import { ReadingDelay, visualHoldMs } from "@/lib/visual-sync";

interface Snapshot {
  tiltX: number;
  tiltY: number;
  energy: number;
  bands: number[];
}

const NUM_BANDS = 8;

const state: Snapshot = {
  tiltX: 0,
  tiltY: 0,
  energy: 0,
  bands: new Array(NUM_BANDS).fill(0),
};

const subscribers = new Set<SVGElement | HTMLElement>();
let analyser: AnalyserNode | null = null;
let timeBuf: Float32Array | null = null;
let freqBuf: Uint8Array | null = null;
/** Per-band [firstBin, lastBin) ranges, LOG-spaced 40Hz→16kHz so each band
 *  tracks a musically meaningful range (sub, bass, low-mids … air) instead
 *  of the old linear split where band 0 held basically the whole mix and
 *  bands 4–7 sat above 11kHz in noise (Gordon: "the vector fx needs to be
 *  more correct in relation to the frequencies in the music"). */
let bandRanges: [number, number][] | null = null;
/** Energy + 8 bands, held back by the speakers' delay (visual-sync.ts). */
const reading = new Float32Array(1 + NUM_BANDS);
const delay = new ReadingDelay(1 + NUM_BANDS);
let raf: number | null = null;

/** Call this from the media player when an analyser becomes available or
 *  changes. Pass `null` to detach. */
export function setAvatarAnalyser(node: AnalyserNode | null): void {
  analyser = node;
  timeBuf = null;
  freqBuf = null;
  bandRanges = null;
}

/** The microphone / synth level BB publishes (same −52 dB floor as the
 *  player's meter), or null when nothing is listening. */
export function micLevel(): number | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as { __bbMicOn?: boolean; __bbMicDb?: number };
  if (w.__bbMicOn !== true || typeof w.__bbMicDb !== "number" || !isFinite(w.__bbMicDb)) return null;
  return Math.max(0, Math.min(1, (w.__bbMicDb + 52) / 52));
}

/** Whether any sound is moving the art right now. */
let soundOn = false;
export function isSoundOn(): boolean { return soundOn; }

function pushToDOM() {
  // Write CSS vars onto every subscribed <svg>. This is the only DOM work
  // per frame — orders cheaper than triggering a React render.
  const e = state.energy.toFixed(3);
  const tx = state.tiltX.toFixed(3);
  const ty = state.tiltY.toFixed(3);
  subscribers.forEach(el => {
    el.style.setProperty("--bb-energy", e);
    el.style.setProperty("--bb-tilt-x", tx);
    el.style.setProperty("--bb-tilt-y", ty);
    for (let b = 0; b < NUM_BANDS; b++) {
      el.style.setProperty(`--bb-band-${b}`, state.bands[b].toFixed(3));
    }
  });
}

function tick() {
  // The player's sound if there is an analyser; a microphone fills in below.
  let heard = 0;
  if (analyser) {
    if (!timeBuf) timeBuf = new Float32Array(analyser.fftSize);
    if (!freqBuf) freqBuf = new Uint8Array(analyser.frequencyBinCount);
    try {
      // ── Energy — behave like a real meter (Gordon: "up to 0dB, full
      // animation amplitude"). Map RMS → dBFS, then a −52 dB floor → 0 and
      // 0 dBFS → 1, so loud passages drive the animation to its full range.
      analyser.getFloatTimeDomainData(timeBuf);
      let sum = 0;
      for (let i = 0; i < timeBuf.length; i++) sum += timeBuf[i] * timeBuf[i];
      const rms = Math.sqrt(sum / timeBuf.length);
      const db = 20 * Math.log10(rms + 1e-6);          // ≈ −∞…0 dBFS
      reading[0] = Math.max(0, Math.min(1, (db + 52) / 52));

      // ── Bands — 8 LOG-spaced buckets, 40Hz→16kHz (sub, bass, low-mids,
      // mids, upper-mids, presence, brilliance, air) so each band follows a
      // range the ear actually hears as distinct. getByteFrequencyData is
      // already dB-scaled (0..255): blend avg+peak, gamma-expand for motion,
      // small lift on the upper bands for the natural HF rolloff.
      analyser.getByteFrequencyData(freqBuf);
      if (!bandRanges) {
        const sr = (analyser.context && (analyser.context as BaseAudioContext).sampleRate) || 44100;
        const nyquist = sr / 2;
        const fLo = 40, fHi = Math.min(16000, nyquist * 0.95);
        const bins = freqBuf.length;
        bandRanges = [];
        for (let b = 0; b < NUM_BANDS; b++) {
          const eLo = fLo * Math.pow(fHi / fLo, b / NUM_BANDS);
          const eHi = fLo * Math.pow(fHi / fLo, (b + 1) / NUM_BANDS);
          const iLo = Math.max(0, Math.floor((eLo / nyquist) * bins));
          const iHi = Math.min(bins, Math.max(iLo + 1, Math.ceil((eHi / nyquist) * bins)));
          bandRanges.push([iLo, iHi]);
        }
      }
      for (let b = 0; b < NUM_BANDS; b++) {
        const [iLo, iHi] = bandRanges[b];
        let bsum = 0, bmax = 0, n = 0;
        for (let i = iLo; i < iHi; i++) { const val = freqBuf[i]; bsum += val; if (val > bmax) bmax = val; n++; }
        const base = n ? (bsum / n * 0.55 + bmax * 0.45) / 255 : 0;   // 0..1, avg+peak blend
        const highGain = 1 + b * 0.05;                                // gentle HF compensation
        reading[1 + b] = Math.max(0, Math.min(1, Math.pow(base, 0.72) * highGain));
      }
      // Wait out the speakers' delay, then react: instant attack (on the beat),
      // slower release, like a meter (Gordon 2026-11-04: "react instantly").
      delay.hold(reading, visualHoldMs(analyser), performance.now());
      heard = reading[0];
      state.energy += (reading[0] - state.energy) * (reading[0] > state.energy ? 0.9 : 0.12);
      for (let b = 0; b < NUM_BANDS; b++) {
        const v = reading[1 + b];
        state.bands[b] += (v - state.bands[b]) * (v > state.bands[b] ? 0.9 : 0.16);
      }
    } catch {
      // Analyser was detached mid-frame — drop it; the mic or silence takes over.
      analyser = null;
    }
  }
  // No player sound: a microphone or the synth, louder than the player, drives
  // it (one level, spread over the bands, lows strongest); otherwise silence
  // pulls every value down to zero and the art stands still.
  const mic = micLevel();
  if (mic !== null && mic > heard) {
    state.energy += (mic - state.energy) * (mic > state.energy ? 0.9 : 0.12);
    for (let b = 0; b < NUM_BANDS; b++) {
      const v = mic * (1 - b * 0.07);
      state.bands[b] += (v - state.bands[b]) * (v > state.bands[b] ? 0.9 : 0.16);
    }
  } else if (!analyser) {
    state.energy += (0 - state.energy) * 0.12;
    for (let b = 0; b < NUM_BANDS; b++) state.bands[b] += (0 - state.bands[b]) * 0.16;
  }
  const loud = state.energy > 0.03;
  if (loud !== soundOn) {
    soundOn = loud;
    window.dispatchEvent(new CustomEvent("bb-sound", { detail: loud }));
  }

  pushToDOM();

  if (subscribers.size > 0 && document.visibilityState !== "hidden") {
    raf = requestAnimationFrame(tick);
  } else {
    raf = null;
  }
}

function ensureRunning() {
  if (raf === null && subscribers.size > 0 && typeof document !== "undefined" && document.visibilityState !== "hidden") {
    raf = requestAnimationFrame(tick);
  }
}

// ── Tilt input — wired once at module load ───────────────────────────────
if (typeof window !== "undefined") {
  const onMouseMove = (e: MouseEvent) => {
    const cx = window.innerWidth / 2;
    const cy = window.innerHeight / 2;
    state.tiltX = Math.max(-1, Math.min(1, (e.clientX - cx) / cx));
    state.tiltY = Math.max(-1, Math.min(1, (e.clientY - cy) / cy));
  };
  const onOrient = (e: DeviceOrientationEvent) => {
    const gamma = e.gamma ?? 0;   // -90..90 left/right tilt
    const beta = e.beta ?? 0;     // -180..180 front/back tilt
    state.tiltX = Math.max(-1, Math.min(1, gamma / 45));
    state.tiltY = Math.max(-1, Math.min(1, (beta - 30) / 45));
  };
  const isCoarse = window.matchMedia?.("(pointer: coarse)").matches;
  if (isCoarse && typeof DeviceOrientationEvent !== "undefined") {
    // iOS 13+ requires explicit permission — wire it to the first tap.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Ctor = DeviceOrientationEvent as any;
    const start = () => window.addEventListener("deviceorientation", onOrient);
    if (typeof Ctor.requestPermission === "function") {
      const requestOnce = async () => {
        try {
          const res = await Ctor.requestPermission();
          if (res === "granted") start();
        } catch { /* user declined — keep mouse-only */ }
        window.removeEventListener("click", requestOnce);
        window.removeEventListener("touchstart", requestOnce);
      };
      window.addEventListener("click", requestOnce, { once: true, passive: true });
      window.addEventListener("touchstart", requestOnce, { once: true, passive: true });
    } else {
      start();
    }
  } else {
    window.addEventListener("mousemove", onMouseMove, { passive: true });
  }

  // Pause when the tab is hidden, resume when visible.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") ensureRunning();
  });
}

/**
 * Subscribe an <svg> element to live tilt + audio updates. Pass a ref
 * pointing at the SVG; CSS variables are written directly onto it.
 * The hook auto-unsubscribes on unmount.
 */
export function useAvatarReactivity(ref: React.RefObject<SVGElement | HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    subscribers.add(el);
    ensureRunning();
    return () => {
      subscribers.delete(el);
      // Clean up vars so a detached element doesn't keep stale styles.
      el.style.removeProperty("--bb-energy");
      el.style.removeProperty("--bb-tilt-x");
      el.style.removeProperty("--bb-tilt-y");
      for (let b = 0; b < NUM_BANDS; b++) {
        el.style.removeProperty(`--bb-band-${b}`);
      }
    };
  }, [ref]);
}
